'use es6';

import { Map as ImmutableMap } from 'immutable';
import { useState } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CardWrapper, CardHeader, CardSection } from 'foundations-components/transitional/components/Card';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import { DATA_REPROCESSING } from 'reporting-data/constants/errorTypes';
import useReportConfig from '../../hooks/useReportConfig';
import AttributionUsageTracker, { ACTIONS, EVENTS } from '../../usageTracker.js';
import { ATTRIBUTION_MODELS } from '../../utils/constants';
import { getIsLimitedAccessRangeType, getIsLimitedAccessStartDate } from '../../utils/dateRange';
import AttributionBuilderLink from './AttributionBuilderLink';
import EmptyState from './EmptyState';
import RecalculatingState from './RecalculatingState';
import Toolbar from './Toolbar';
import RevenueAttributionDateRangePicker from './RevenueAttributionDateRangePicker';
import useAttributionDateRange from '../../hooks/useAttributionDateRange';
import CampaignRevenueAttributionLimitedAccessEmptyState from './CampaignRevenueAttributionLimitedAccessEmptyState';
import RevenueUpdateInfo from './RevenueUpdateInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const langKey = key => `components.revenueAttributionCard.${key}`;
const DEFAULT_DATE_RANGE = {
  rangeType: 'THIS_MONTH'
};
function RevenueAttributionCardV2({
  allowDrilldown = false,
  attributionLinkParams,
  dateRange: dateRangeFromProps,
  filters,
  hasRevenueAttributionAccess,
  hasRevenueAttributionLimitedAccess = false,
  withoutLimitedDateRangePicker,
  customSaveReportButtonProps,
  learnMoreUrls = null,
  screen,
  tooltipKey,
  tooltipOptions = null,
  useInlineLabels = true,
  forExport = false,
  withUpdateDateRangeUpgrade = false,
  onAttributionModelChange = () => {}
}) {
  const [dateRangeState, setDateRangeState] = useState(DEFAULT_DATE_RANGE);
  const [attributionModel, setAttributionModel] = useState(hasRevenueAttributionLimitedAccess ? ATTRIBUTION_MODELS.LINEAR_PRO : ATTRIBUTION_MODELS.LINEAR);
  const [hasReportResolved, setHasReportResolved] = useState(false);
  const dateRange = useAttributionDateRange({
    dateRangeFromProps,
    dateRangeState,
    forExport,
    hasRevenueAttributionAccess,
    hasRevenueAttributionLimitedAccess,
    withoutLimitedDateRangePicker
  });
  const reportConfig = useReportConfig({
    filters,
    attributionModel,
    dateRange,
    hasRevenueAttributionLimitedAccess,
    learnMoreUrls,
    screen
  });
  const handleModelChange = newModel => {
    AttributionUsageTracker.track(EVENTS.REVENUE_ATTRIBUTION_CARD, {
      action: ACTIONS.CHANGED_MODEL,
      screen,
      fromModel: attributionModel,
      toModel: newModel
    });
    setAttributionModel(newModel);
    onAttributionModelChange(newModel);
  };
  const renderNoData = () => {
    return /*#__PURE__*/_jsx(EmptyState, {
      translationKey: hasRevenueAttributionLimitedAccess ? 'noDataPro' : 'noDataEnt',
      hasRevenueAttributionLimitedAccess: hasRevenueAttributionLimitedAccess
    });
  };
  const renderError = error => {
    if (error && error.statusText === DATA_REPROCESSING) {
      return /*#__PURE__*/_jsx(RecalculatingState, {});
    }
    return renderNoData('insufficientData')();
  };
  const renderTitle = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: langKey(hasRevenueAttributionLimitedAccess ? 'limitedAccess.title' : 'title')
    }), tooltipKey && /*#__PURE__*/_jsx(HelpIcon, {
      "data-test-id": "revenue-attribution-card-tooltip",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: tooltipKey,
        options: tooltipOptions
      }),
      tooltipPlacement: "right bottom",
      size: "200",
      color: "icon-core-subtle",
      marginInlineStart: "200"
    })]
  });
  const isLimitedAccessRangeType = getIsLimitedAccessRangeType(dateRange);
  const isLimitedAccessStartDate = getIsLimitedAccessStartDate(dateRange);
  const showEmptyState = hasRevenueAttributionLimitedAccess && (isLimitedAccessRangeType || isLimitedAccessStartDate);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    "data-test-id": "revenue-attribution-card-v2",
    children: [/*#__PURE__*/_jsx(CardHeader, {
      title: renderTitle(),
      titleSize: "lg",
      toolbar: hasRevenueAttributionLimitedAccess ? !withoutLimitedDateRangePicker && /*#__PURE__*/_jsx(RevenueAttributionDateRangePicker, {
        hasRevenueAttributionLimitedAccess: hasRevenueAttributionLimitedAccess,
        dateRange: dateRange,
        setDateRangeState: setDateRangeState,
        screen: screen
      }) : /*#__PURE__*/_jsx(Toolbar, {
        attributionModel: attributionModel,
        onAttributionModelChange: handleModelChange,
        hasRevenueAttributionAccess: hasRevenueAttributionAccess
      })
    }), !hasRevenueAttributionAccess && !hasRevenueAttributionLimitedAccess && /*#__PURE__*/_jsx(RevenueAttributionDateRangePicker, {
      hasRevenueAttributionLimitedAccess: hasRevenueAttributionLimitedAccess,
      dateRange: dateRange,
      setDateRangeState: setDateRangeState,
      screen: screen
    }), /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsxs("div", {
        style: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          textAlign: 'center',
          width: '100%'
        },
        "data-loading": !hasReportResolved,
        children: [showEmptyState ? /*#__PURE__*/_jsx(CampaignRevenueAttributionLimitedAccessEmptyState, {
          asExport: forExport,
          config: reportConfig.get('config'),
          isLimitedAccessRangeType: isLimitedAccessRangeType,
          isLimitedAccessStartDate: isLimitedAccessStartDate
        }) : /*#__PURE__*/_jsx(PlatformReportResolver, {
          allowDrilldown: allowDrilldown,
          errorRenderer: renderError,
          noDataRenderer: renderNoData,
          report: reportConfig,
          customSaveReportButtonProps: customSaveReportButtonProps,
          drilldownEventCallback: (eventType, drilldownEvent) => {
            if (drilldownEvent && eventType === 'DRILLDOWN_OPENED') {
              AttributionUsageTracker.track(EVENTS.REVENUE_ATTRIBUTION_CARD, {
                action: ACTIONS.OPENED_MODAL,
                screen,
                modal: `${hasRevenueAttributionLimitedAccess ? 'Revenue' : 'Revenue attribution'} - ${drilldownEvent.report.get('name')}`
              });
            }
          },
          useInlineLabels: useInlineLabels,
          onReportResolve: () => setHasReportResolved(true),
          customSubheader: hasRevenueAttributionLimitedAccess && /*#__PURE__*/_jsx(RevenueUpdateInfo, {})
        }), !forExport && /*#__PURE__*/_jsx(AttributionBuilderLink, {
          paddingBlockStart: "400",
          screen: screen,
          urlQueryParams: attributionLinkParams,
          hasRevenueAttributionAccess: hasRevenueAttributionAccess,
          hasRevenueAttributionLimitedAccess: hasRevenueAttributionLimitedAccess,
          withUpdateDateRangeUpgrade: withUpdateDateRangeUpgrade
        }, withUpdateDateRangeUpgrade)]
      })
    })]
  });
}
RevenueAttributionCardV2.propTypes = {
  allowDrilldown: PropTypes.bool.isRequired,
  filters: PropTypes.array.isRequired,
  dateRange: PropTypes.instanceOf(ImmutableMap).isRequired,
  hasRevenueAttributionAccess: PropTypes.bool.isRequired,
  hasRevenueAttributionLimitedAccess: PropTypes.bool,
  attributionLinkParams: PropTypes.object,
  learnMoreUrls: PropTypes.oneOf([PropTypes.object, PropTypes.string]),
  screen: PropTypes.string.isRequired,
  tooltipKey: PropTypes.string,
  tooltipOptions: PropTypes.object,
  customSaveReportButtonProps: PropTypes.object,
  useInlineLabels: PropTypes.bool,
  forExport: PropTypes.bool,
  withoutLimitedDateRangePicker: PropTypes.bool,
  withUpdateDateRangeUpgrade: PropTypes.bool,
  onAttributionModelChange: PropTypes.func
};
export default RevenueAttributionCardV2;